import { apiClient } from '@/shared/lib/apiClient';
import type { ApiResponse } from '@/types';
import type {
  CreditsTransaction,
  CreditsWallet,
  EligibleBooking,
  MerchOptions,
  MerchRequest,
  MerchRequestInput,
  Paginated,
  PhotoType,
  PhotoView,
  ReferralRow,
  ReferralStatus,
  RewardActivityView,
  RewardsSummary,
} from '../types';

export interface ReferralQuery {
  page: number;
  limit: number;
  search?: string;
  status?: ReferralStatus | '';
  from?: string;
  to?: string;
}

const unwrap = <T>(response: { data: ApiResponse<T> }) => response.data.data as T;

export const growthApi = {
  getSummary: async () => unwrap(await apiClient.get<ApiResponse<RewardsSummary>>('/growth/me/summary')),

  getReferrals: async (query: ReferralQuery) =>
    unwrap(await apiClient.get<ApiResponse<Paginated<ReferralRow>>>('/growth/me/referrals', { params: query })),

  getReferral: async (id: string) =>
    unwrap(await apiClient.get<ApiResponse<ReferralRow>>(`/growth/me/referrals/${id}`)),

  getActivities: async () =>
    unwrap(await apiClient.get<ApiResponse<RewardActivityView[]>>('/growth/me/activities')),

  getCredits: async () => unwrap(await apiClient.get<ApiResponse<CreditsWallet>>('/growth/me/credits')),

  getTransactions: async (page: number, limit: number) =>
    unwrap(
      await apiClient.get<ApiResponse<Paginated<CreditsTransaction>>>('/growth/me/credits/transactions', {
        params: { page, limit },
      })
    ),

  getMerchOptions: async () =>
    unwrap(await apiClient.get<ApiResponse<MerchOptions>>('/growth/me/merch/options')),

  getMerchRequests: async () =>
    unwrap(await apiClient.get<ApiResponse<MerchRequest[]>>('/growth/me/merch/requests')),

  createMerchRequest: async (input: MerchRequestInput) =>
    unwrap(await apiClient.post<ApiResponse<MerchRequest>>('/growth/me/merch/requests', input)),

  cancelMerchRequest: async (id: string) =>
    unwrap(await apiClient.patch<ApiResponse<MerchRequest>>(`/growth/me/merch/requests/${id}/cancel`)),

  confirmMerchReceived: async (id: string) =>
    unwrap(await apiClient.patch<ApiResponse<MerchRequest>>(`/growth/me/merch/requests/${id}/received`)),

  getPhotos: async () => unwrap(await apiClient.get<ApiResponse<PhotoView[]>>('/growth/me/photos')),

  getEligibleBookings: async () =>
    unwrap(await apiClient.get<ApiResponse<EligibleBooking[]>>('/growth/me/photos/eligible-bookings')),

  uploadPhoto: async (input: { file: File; type: PhotoType; bookingId?: string; consent: boolean }) => {
    const form = new FormData();
    form.append('file', input.file);
    form.append('type', input.type);
    if (input.bookingId) form.append('bookingId', input.bookingId);
    form.append('consent', String(input.consent));
    return unwrap(await apiClient.post<ApiResponse<PhotoView>>('/growth/me/photos', form));
  },
};

export const growthKeys = {
  all: ['growth'] as const,
  summary: () => [...growthKeys.all, 'summary'] as const,
  referrals: (query: ReferralQuery) => [...growthKeys.all, 'referrals', query] as const,
  referral: (id: string) => [...growthKeys.all, 'referral', id] as const,
  activities: () => [...growthKeys.all, 'activities'] as const,
  transactions: (page: number, limit: number) => [...growthKeys.all, 'transactions', page, limit] as const,
  merchOptions: () => [...growthKeys.all, 'merch-options'] as const,
  merchRequests: () => [...growthKeys.all, 'merch-requests'] as const,
  photos: () => [...growthKeys.all, 'photos'] as const,
  eligibleBookings: () => [...growthKeys.all, 'eligible-bookings'] as const,
};

/** Server message from an axios error, or a fallback. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  return (
    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback
  );
}
